import React, { useState, useCallback } from "react";
import { Card, Button, Table, Space, message, Badge, Divider, Spin, Alert } from "antd";
import { PlusOutlined, EditOutlined, EyeOutlined, CheckOutlined, StopOutlined } from "@ant-design/icons";
import { useLoadList } from "../../../hooks";
import { useTableSelection } from "../../../hooks";
import { contractManagementService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import BusinessPartnerFilter from "./components/BusinessPartnerFilter";
import BusinessPartnerDrawer from "./components/BusinessPartnerDrawer";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { BUSINESS_PARTNER_PERMISSIONS, DRAWER_MODES } from "./constants";
import { runBatchStatusChange } from "./lib/batchStatusChange";

const BATCH_ACTIONS = {
    activate: { label: "启用", request: (id, config) => contractManagementService.businessPartner.activateBusinessPartner(id, config) },
    deactivate: { label: "停用", request: (id, config) => contractManagementService.businessPartner.deactivateBusinessPartner(id, config) },
};

const buildFilterParams = (values) => {
    const params = {};
    const searchTerm = typeof values.search_term === "string" ? values.search_term.trim() : "";
    if (searchTerm) {
        params.search_term = searchTerm;
    }
    if (values.is_active !== undefined && values.is_active !== null) {
        params.is_active = values.is_active;
    }
    return params;
};

const isSameFilter = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const BusinessPartnerList = () => {
    const { hasPermission, loading: authLoading } = useAuth();
    const canUpdate = hasPermission(BUSINESS_PARTNER_PERMISSIONS.UPDATE);
    const canActivate = hasPermission(BUSINESS_PARTNER_PERMISSIONS.ACTIVATE);
    const canDeactivate = hasPermission(BUSINESS_PARTNER_PERMISSIONS.DEACTIVATE);
    const canBatch = canActivate || canDeactivate;

    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [currentPartner, setCurrentPartner] = useState(null);
    const [drawerMode, setDrawerMode] = useState(DRAWER_MODES.ADD);
    const [batchRunning, setBatchRunning] = useState(false);

    const loadCustomerList = useCallback((params) => {
        return contractManagementService.businessPartner.getBusinessPartnerList(params, { _silentError: true });
    }, []);

    const {
        data,
        isLoading,
        error: loadError,
        refetch,
    } = useLoadList("客户", loadCustomerList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
        partner_type: "CUSTOMER",
    });

    const isRowSelectionDisabled = useCallback(() => !canBatch, [canBatch]);

    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection, handleRowSelectionChange } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
        isRowSelectionDisabled,
    });

    const openDrawer = (mode, record) => {
        setCurrentPartner(record);
        setDrawerMode(mode);
        setDrawerVisible(true);
    };

    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "客户编码",
            dataIndex: "code",
            key: "code",
            width: 120,
        },
        {
            title: "英文名称",
            dataIndex: "name",
            key: "name",
            width: 280,
            ellipsis: true,
        },
        {
            title: "中文名称",
            dataIndex: "name_cn",
            key: "name_cn",
            width: 280,
            ellipsis: true,
        },
        {
            title: "联系人",
            dataIndex: "contact_person",
            key: "contact_person",
            width: 120,
            ellipsis: true,
        },
        {
            title: "联系邮箱",
            dataIndex: "contact_email",
            key: "contact_email",
            width: 180,
            ellipsis: true,
        },
        {
            title: "联系电话",
            dataIndex: "contact_phone",
            key: "contact_phone",
            width: 100,
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 80,
            render: (is_active) => <Badge className="status-badge" status={is_active ? "success" : "error"} text={is_active ? "启用" : "停用"} />,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 160,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 120,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <Button
                        type="link"
                        size="small"
                        icon={canUpdate ? <EditOutlined /> : <EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            openDrawer(canUpdate ? DRAWER_MODES.EDIT : DRAWER_MODES.VIEW, record);
                        }}
                    >
                        {canUpdate ? "编辑" : "查看"}
                    </Button>
                </Space>
            ),
        },
    ];

    const handleSelectAllVisible = useCallback(() => {
        const visibleRows = data?.items || [];
        handleRowSelectionChange(
            visibleRows.map((item) => item.id),
            visibleRows,
        );
    }, [data?.items, handleRowSelectionChange]);

    const handleTableChange = useCallback(
        (newPagination) => {
            if (newPagination.current !== pagination.current || newPagination.pageSize !== pagination.pageSize) {
                clearSelection();
            }
            setPagination(newPagination);
        },
        [pagination, clearSelection],
    );

    const applyFilter = useCallback(
        (params) => {
            clearSelection();
            if (isSameFilter(params, filterParams) && pagination.current === 1) {
                refetch();
                return;
            }
            setFilterParams(params);
            setPagination((prev) => ({ ...prev, current: 1 }));
        },
        [clearSelection, filterParams, pagination, refetch],
    );

    const handleSearch = useCallback((values) => applyFilter(buildFilterParams(values)), [applyFilter]);

    const handleReset = useCallback(() => applyFilter({}), [applyFilter]);

    const handleAdd = () => openDrawer(DRAWER_MODES.ADD, null);

    const handleBatchStatusChange = async (actionKey) => {
        const action = BATCH_ACTIONS[actionKey];
        if (selectedRowKeys.length === 0) {
            message.warning(`请选择要${action.label}的客户`);
            return;
        }

        setBatchRunning(true);
        try {
            const { succeededIds, failedIds, reasons } = await runBatchStatusChange(selectedRowKeys, action.request);
            const reasonText = reasons.join("；");

            if (failedIds.length === 0) {
                message.success(`已${action.label} ${succeededIds.length} 个客户`);
                clearSelection();
            } else {
                const failedIdSet = new Set(failedIds);
                handleRowSelectionChange(
                    failedIds,
                    selectedRows.filter((row) => failedIdSet.has(row.id)),
                );
                if (succeededIds.length === 0) {
                    message.error(`${action.label}失败：${reasonText}`, 6);
                } else {
                    message.warning(`已${action.label} ${succeededIds.length} 个，失败 ${failedIds.length} 个（已保留勾选）：${reasonText}`, 6);
                }
            }
            refetch();
        } finally {
            setBatchRunning(false);
        }
    };

    const handleDrawerSuccess = () => {
        setDrawerVisible(false);
        setCurrentPartner(null);
        refetch();
    };

    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">客户</div>
                <div className="page-action-compact">
                    <PermissionGuard permission={BUSINESS_PARTNER_PERMISSIONS.CREATE}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            新增客户
                        </Button>
                    </PermissionGuard>
                </div>
            </div>

            <Card className="filter-card">
                <BusinessPartnerFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            <Card className="data-card">
                {loadError && (
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="客户列表加载失败"
                        description={loadError}
                        action={
                            <Button size="small" onClick={() => refetch()}>
                                重试
                            </Button>
                        }
                    />
                )}
                <div style={{ position: "relative" }}>
                    {selectedRowKeys.length > 0 && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                zIndex: 10,
                                padding: "6px 12px",
                                background: "linear-gradient(90deg, rgba(245, 245, 245, 1) 0%, rgba(245, 245, 245,1) 56%, rgba(245, 245, 245, 0) 60%)",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                pointerEvents: "none",
                            }}
                        >
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", width: "60%", pointerEvents: "auto" }}>
                                <span style={{ marginRight: 8, fontSize: "12px" }}>
                                    已选择 <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 个客户
                                </span>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <Button size="small" type="link" onClick={handleSelectAllVisible} style={{ padding: "0 4px" }}>
                                    全选当前页
                                </Button>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <Button size="small" type="link" onClick={clearSelection} style={{ padding: "0 4px" }}>
                                    取消选择
                                </Button>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                {canActivate && (
                                    <Button size="small" type="primary" ghost icon={<CheckOutlined />} loading={batchRunning} onClick={() => handleBatchStatusChange("activate")} style={{ fontSize: "12px", marginRight: "8px" }}>
                                        批量启用
                                    </Button>
                                )}
                                {canDeactivate && (
                                    <Button size="small" type="primary" ghost icon={<StopOutlined />} loading={batchRunning} onClick={() => handleBatchStatusChange("deactivate")} style={{ fontSize: "12px", marginRight: "8px" }}>
                                        批量停用
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    <Table
                        size="small"
                        ref={tableRef}
                        columns={columns}
                        dataSource={data?.items || []}
                        loading={isLoading || batchRunning}
                        rowKey="id"
                        locale={{ emptyText: loadError ? "加载失败" : "暂无客户" }}
                        rowSelection={
                            canBatch
                                ? {
                                      ...rowSelection,
                                      columnWidth: 6,
                                  }
                                : null
                        }
                        pagination={{
                            ...pagination,
                            total: data?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条记录`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1500 }}
                        onRow={getRowProps}
                    />
                </div>
            </Card>

            <BusinessPartnerDrawer
                visible={drawerVisible}
                mode={drawerMode}
                initialValues={currentPartner}
                onSuccess={handleDrawerSuccess}
                onCancel={() => {
                    setDrawerVisible(false);
                    setCurrentPartner(null);
                }}
            />
        </div>
    );
};

export default BusinessPartnerList;
